import { Button } from "@msuite/ui/button";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { type CellContext, RowActionsWrapper } from "@msuite/ui/table";
import { Tooltip } from "@msuite/ui/tooltip";
import type { IncomingInvoiceMyApprovalListItem } from "@msuite/utils/types";
import {
	IncomingInvoiceDocumentPreviewButton,
	IncomingInvoiceDuplicateActions,
	incomingInvoiceReviewPath,
} from "@/components/incoming-invoices";
import { ReviewApprovalButton } from "@/components/incoming-invoices/review-approval-button";
import { useDecideIncomingInvoiceApproval } from "@/hooks/use-decide-incoming-invoice-approval";

/**
 * Everything an approval needs without leaving the list: the PDF, the review while it is pending —
 * or taking the decision back once it is not, unless the invoice is paid. The row itself opens the
 * invoice on its review tab. A decision moves the row to the other filter.
 */
export function RowActions({ cell }: CellContext<IncomingInvoiceMyApprovalListItem, "actions">) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const { decide, isDeciding } = useDecideIncomingInvoiceApproval();

	/** Functions */
	async function handleOnTakeBack() {
		await decide(row.incomingInvoiceId, row.id, { state: "pending", comment: null });
	}

	/** Computed */
	const row = cell.row.original;
	// A duplicate is settled by merging it or declaring it none, never approved or rejected.
	const isDuplicate = row.invoiceState === "duplicate";
	const isPending = row.state === "pending" && !isDuplicate;
	// A decision is taken back only while nobody after it in the chain has decided.
	const canTakeBack = row.canTakeBack && !isDuplicate && row.invoiceState !== "paid";

	/** Render */
	return (
		<RowActionsWrapper to={incomingInvoiceReviewPath(row.incomingInvoiceId)}>
			{row.fileId && <IncomingInvoiceDocumentPreviewButton fileId={row.fileId} />}
			{isDuplicate && (
				<IncomingInvoiceDuplicateActions
					incomingInvoiceId={row.incomingInvoiceId}
					variant="icons"
				/>
			)}
			{isPending && (
				<ReviewApprovalButton
					incomingInvoiceId={row.incomingInvoiceId}
					approvalId={row.id}
					turn={row.turn}
					mayDecideAhead={row.assignment === "default"}
					skipCount={row.openStepsBefore}
				/>
			)}
			{canTakeBack && (
				<Tooltip label={t("incoming_invoices.take_back_decision")}>
					<Button
						icon={<Icon name="Undo2" />}
						size="xxs"
						variant="ghost"
						isLoading={isDeciding}
						onClick={handleOnTakeBack}
						data-cy="take-back-incoming-invoice-decision-button"
					/>
				</Tooltip>
			)}
		</RowActionsWrapper>
	);
}
